/**
 * The collection rows — the Tech Features beat, applied to one row at a time.
 *
 * THIS IS THE `luna-tech.js` CHOREOGRAPHY, NOT A NEW ONE. Same four steps, same
 * technique, same delays: the dot lights, a wire draws out of it, the panel's border
 * follows the wire round, the copy lands. The stylesheet in `luna-collection-rows.liquid`
 * carries the same numbers as the one in `luna-tech.liquid` — 0 / 140 / 480 / 780ms —
 * and the drawing is the same `stroke-dashoffset` animation over paths this file writes
 * from the boxes as they actually landed.
 *
 * WHAT IS DIFFERENT, and why it could not simply reuse that file:
 *
 *   - `luna-tech.js` binds to `[data-luna-tech]`, insists on exactly four cards, and
 *     will not start without a battery, a ring and a core. None of those exist here.
 *   - Tech runs its four beats off ONE timer, because its four cards are on screen
 *     together. These rows are ~550px apart and cross the viewport one at a time, so
 *     each row is its own trigger. A shared timer would fire rows five and six while
 *     they were still far below the fold.
 *   - Tech scrubs backwards on phones because its cards share one pinned slot. Nothing
 *     here is pinned, so every row is ONE-WAY at every width: it plays once and holds.
 *
 * ONE-WAY IS THE WHOLE CONTRACT. `.is-in` is added and never removed, and `played`
 * guards the row so a second trigger is a no-op. Scrolling back up must not rewind
 * anything.
 *
 * THE RESTING STATE IS THE FINISHED STATE. Every rule that hides anything is written
 * under `[data-luna-rows-live]`, and only this file sets that attribute. So no-JS,
 * old-browser and reduced-motion all land on the same fully-visible page without a
 * second layout existing anywhere.
 */
(function () {
  const section = document.querySelector('[data-luna-rows]');
  if (!section) return;

  const list = section.querySelector('[data-luna-rows-list]');
  const items = Array.from(section.querySelectorAll('[data-luna-rows-item]'));
  if (!list || !items.length) return;

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  /* Matches the stylesheet's breakpoint. The layout it selects changes the geometry AND
     the beat, so both sides of this file have to ask the same question. */
  const stackedQuery = window.matchMedia('(max-width: 749px)');

  /*
   * DESKTOP beats, in milliseconds — the stylesheet's chain, restated so the backstop
   * and the observer agree on when a row is finished. Copied from `luna-tech.liquid`.
   *
   * MOBILE drops the wire, because the panel overlaps the picture there and a leader
   * line would have to cross the thing it is pointing at. The frame is pulled forward
   * into the slot the wire used to occupy rather than left to wait it out: a 480ms gap
   * with nothing drawing in it reads as a stall, not as restraint. The dot and the copy
   * keep their own timings, so the character survives and only the missing step's hole
   * is closed.
   *
   * These are the numbers the stylesheet animates. If either moves, the other has to.
   */
  const STROKE = 1.5;

  let frame = 0;
  const played = items.map(() => false);

  /*
   * ONE SVG PER ROW, not one over the whole list — and this is a correction to the
   * obvious way of copying `luna-tech.js`, which draws all eight of its paths into a
   * single overlay sized to its board.
   *
   * That works there because the board is one screen and everything on it settles at
   * once. Here the list is nearly 5000px tall and the pictures load lazily, so a
   * picture arriving in row five changes the height of the list and every path already
   * drawn against the old box — including the rows a visitor has scrolled past and seen
   * — silently moves off its panel. Giving each row its own overlay makes the
   * coordinates row-local, so a row that has been drawn stays correct no matter what
   * happens below it.
   */
  const rows = items.map((item) => ({
    item,
    card: item.querySelector('[data-luna-rows-card]'),
    svg: item.querySelector('[data-luna-rows-wires]'),
    panel: item.querySelector('[data-luna-rows-panel]'),
    dotWide: item.querySelector('[data-luna-rows-dot="wide"]'),
    dotTall: item.querySelector('[data-luna-rows-dot="tall"]'),
    line: item.querySelector('[data-luna-rows-line]'),
    frame: item.querySelector('[data-luna-rows-frame]'),
  }));

  if (rows.some((r) => !r.card || !r.svg || !r.panel || !r.dotWide || !r.dotTall || !r.line || !r.frame)) {
    return;
  }

  /*
   * The corner radius is READ, not copied. It is `clamp(18px, 2vw, 26px)` in the
   * stylesheet, so there is no single number to hard-code, and this project has already
   * written down what happens when a constant is kept in two places — the two drift and
   * the failure is silent. `luna-tech.js` gets away with a literal 18 only because its
   * cards have a fixed radius.
   */
  const radiusOf = (el) => {
    const r = parseFloat(getComputedStyle(el).borderTopLeftRadius);
    return Number.isFinite(r) ? r : 18;
  };

  /*
   * A rounded rectangle as ONE OPEN PATH, starting at the corner the wire arrives beside
   * and running clockwise from there.
   *
   * LIFTED VERBATIM FROM `luna-tech.js`. It is duplicated rather than shared because the
   * Luna scripts on this theme are stand-alone IIFEs with no module graph between them;
   * if that ever changes, this is the first thing that should move into a shared file.
   * Fix a bug in one and fix it in the other.
   *
   * The start corner is the only reason this is not a `<rect>`: the border is drawn by
   * animating `stroke-dashoffset` to zero, which always begins at the path's first
   * point, so choosing that point is how the border appears to grow out of the wire.
   *
   * `corner` is 0 top-left, 2 top-right, 4 bottom-right, 6 bottom-left.
   */
  const roundedRect = (x, y, w, h, r, corner) => {
    const radius = Math.min(r, w / 2, h / 2);
    const right = x + w;
    const bottom = y + h;
    const arc = (px, py) => 'A' + radius + ' ' + radius + ' 0 0 1 ' + px + ' ' + py;

    const seg = [
      'L' + (right - radius) + ' ' + y,
      arc(right, y + radius),
      'L' + right + ' ' + (bottom - radius),
      arc(right - radius, bottom),
      'L' + (x + radius) + ' ' + bottom,
      arc(x, bottom - radius),
      'L' + x + ' ' + (y + radius),
      arc(x + radius, y),
    ];

    const starts = {
      0: [x + radius, y],
      2: [right, y + radius],
      4: [right - radius, bottom],
      6: [x, bottom - radius],
    };

    const from = starts[corner] || starts[0];
    let d = 'M' + from[0] + ' ' + from[1];
    for (let i = 0; i < seg.length; i += 1) d += seg[(corner + i) % seg.length];
    return d;
  };

  /* ------------------------------------------------------------------- geometry */

  const draw = () => {
    const stacked = stackedQuery.matches;
    const half = STROKE / 2;

    for (const row of rows) {
      const box = row.card.getBoundingClientRect();
      if (!box.width || !box.height) continue;

      /* One user unit per CSS pixel, so stroke widths and dash lengths need no scaling. */
      row.svg.setAttribute('viewBox', '0 0 ' + box.width + ' ' + box.height);

      const panel = row.panel.getBoundingClientRect();
      const dot = (stacked ? row.dotTall : row.dotWide).getBoundingClientRect();
      if (!panel.width || !dot.width) continue;

      const px = panel.left - box.left;
      const py = panel.top - box.top;
      const dx = dot.left - box.left + dot.width / 2;

      let corner;

      if (stacked) {
        /*
         * No leader at all. The dot sits on the panel's own top-left corner, so the
         * border simply grows out from under it — the wire's job, done by adjacency.
         * The path is emptied rather than left stale, or a landscape phone rotated to
         * portrait would keep a line drawn for the other layout.
         */
        row.line.setAttribute('d', '');
        row.line.style.setProperty('--len', '0');
        corner = 0;
      } else {
        /*
         * A STRAIGHT RUN, gap-width, at the panel's middle.
         *
         * `align-items: center` puts the panel's centre on the picture's centre, and the
         * dot is pinned to the picture's inner edge at 50% — so both ends of this line are
         * already on the same horizontal, and the line is the gap between the two cards
         * and nothing more.
         *
         * Both ends are drawn at `y` rather than one of them at the dot's own centre, so
         * the line is exactly horizontal: the two centres
         * agree to within half a pixel, and half a pixel of slope on a 1.25px stroke is
         * visible as a soft edge on one side.
         *
         * (This replaced a diagonal-and-knee leader copied from `luna-tech.js`. That shape
         * exists there to reconcile four dots at four heights with cards pinned at two;
         * with one level card per dot there was nothing for the bend to reconcile.)
         */
        const y = py + panel.height / 2;
        /* Which of the panel's edges faces the picture. */
        const enterLeft = dx < px;
        const edge = enterLeft ? px : px + panel.width;

        row.line.setAttribute('d', 'M' + dx + ' ' + y + 'L' + edge + ' ' + y);
        row.line.style.setProperty('--len', String(row.line.getTotalLength()));

        /* Start the border beside where the leader lands: entering the left edge, grow up
           it; entering the right edge, grow down it. */
        corner = enterLeft ? 6 : 2;
      }

      /* Inset by half the stroke so the drawn border sits ON the panel's edge rather
         than straddling it. */
      row.frame.setAttribute(
        'd',
        roundedRect(px + half, py + half, panel.width - STROKE, panel.height - STROKE, radiusOf(row.panel), corner)
      );
      row.frame.style.setProperty('--len', String(row.frame.getTotalLength()));
    }
  };

  /* ---------------------------------------------------------------------- state */

  const play = (i) => {
    if (played[i]) return;
    played[i] = true;
    /* Paths are written before the class lands, or the first row would animate a dash
       pattern measured against an empty path. */
    rows[i].item.classList.add('is-in');
  };

  /*
   * THE BACKSTOP. An IntersectionObserver is never the only thing that can start work on
   * this project — the in-app preview pane reports `visibilityState: hidden` and its
   * observers genuinely never fire, and a row that only an observer can reveal would
   * stay at opacity 0 for ever with nothing able to open it.
   *
   * Scroll is listened for in the CAPTURE phase on `document` because scroll events do
   * not bubble, and above 750px this theme scrolls `.page-wrapper` rather than the
   * document — a listener on `window` would never hear a thing. Same as `luna-header.js`
   * and `luna-hero.js`.
   */
  const check = () => {
    frame = 0;
    const view = window.innerHeight || document.documentElement.clientHeight;
    for (let i = 0; i < rows.length; i += 1) {
      if (played[i]) continue;
      const rect = rows[i].item.getBoundingClientRect();
      if (rect.top < view * 0.85 && rect.bottom > 0) play(i);
    }
  };

  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(check);
  };

  let relayoutFrame = 0;
  const relayout = () => {
    if (relayoutFrame) cancelAnimationFrame(relayoutFrame);
    relayoutFrame = requestAnimationFrame(() => {
      relayoutFrame = 0;
      draw();
    });
  };

  /* ---------------------------------------------------------------------- start */

  let observer = null;
  let resizeObserver = null;

  const start = () => {
    section.setAttribute('data-luna-rows-live', '');
    draw();

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const i = items.indexOf(entry.target);
            if (i >= 0) {
              play(i);
              observer.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
      );
      for (const item of items) observer.observe(item);
    }

    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', relayout, { passive: true });

    /*
     * The pictures are lazy, so the panel's position is not final until they land — an
     * image arriving below a row lengthens the list and every path drawn against the old
     * box is then wrong. ResizeObserver on the list catches exactly that, and also the
     * font swap, which moves panel heights by a few pixels.
     */
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(relayout);
      resizeObserver.observe(list);
    }

    schedule();
  };

  const stop = () => {
    section.removeAttribute('data-luna-rows-live');
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    document.removeEventListener('scroll', schedule, { capture: true });
    window.removeEventListener('resize', relayout);
    /* Leave `.is-in` alone: it only ever means "finished", and the resting stylesheet
       shows everything anyway once the live attribute is gone. */
  };

  const sync = () => (motionQuery.matches ? stop() : start());

  const on = (query, handler) => {
    if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
    else if (typeof query.addListener === 'function') query.addListener(handler);
  };

  on(motionQuery, sync);
  /* Crossing the breakpoint swaps which dot the leader starts from and whether there is
     a leader at all, so the paths have to be rewritten — not just re-measured. */
  on(stackedQuery, relayout);

  sync();
})();
